import './styles.scoped.css'
import {useEffect} from 'react'
import {Typography, Button, Modal, FormControl, Select, MenuItem, FormHelperText} from '@mui/material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {accountApi} from '../../../../services'
import {useForm, Controller} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {parseError} from '../../../../utils/helpers'

export default ({open, setOpen, roles, role, reloadRoles}) => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                target_role_id: yup.string().required('New Role is required'),
            }),
        ),
    })

    useEffect(() => {
        form.reset()
    }, [open])

    const handleRemoveRole = () => {
        setLoading(true)
        accountApi
            .deleteRole(role?.id)
            .then(() => {
                setLoading(false)
                setOpen(false)
                setMessageBox({
                    status: 'success',
                    title: 'Role Removed',
                    description: 'The role has been successfully removed from your team settings.',
                })
                reloadRoles()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const handleMoveAndRemoveRole = async data => {
        try {
            setLoading(true)
            await accountApi.moveAndRemoveRole(role?.id, data)
            await accountApi.deleteRole(role?.id)
            setLoading(false)
            setOpen(false)
            setMessageBox({
                status: 'success',
                title: 'Role Removed & Members Reassigned',
                description: `The role has been removed, and all assigned team members have been moved to ${
                    roles.find(role => role.id == data?.target_role_id)?.display_name
                }.`,
            })
            reloadRoles()
        } catch (error) {
            setLoading(false)
            setOpen(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    return (
        <Modal open={open}>
            <div className="modal-container">
                <div className="modal">
                    {role?.users_count > 0 ? (
                        <>
                            <div className="modal-title">
                                <Typography variant="h6" color="text.primary">
                                    This Role Has Assigned Members
                                </Typography>
                            </div>
                            <br />
                            <Typography variant="body2" color="text.primary" style={{textAlign: 'center'}}>
                                This role is currently assigned to {role?.users_count ?? 0} team member(s). To proceed, you need to move them to a
                                different role before removing this one.
                            </Typography>
                            <br />
                            <Typography variant="inputLabel" color="text.secondary">
                                New Role{' '}
                                <Typography variant="inputLabel" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <FormControl fullWidth error={!!form.formState.errors['target_role_id']}>
                                <Controller
                                    {...form.register('target_role_id')}
                                    control={form.control}
                                    render={({field}) => (
                                        <Select
                                            {...field}
                                            value={field.value ?? ''}
                                            onChange={e => field.onChange(e.target.value)}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                            }}
                                            displayEmpty
                                            renderValue={selected => {
                                                if (selected === '') {
                                                    return <span style={{color: '#aaa'}}>Select role to re-assign</span>
                                                }
                                                return roles.find(item => item.id === selected)?.display_name ?? selected
                                            }}>
                                            {roles.map(item => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.display_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                <FormHelperText>{form.formState.errors['target_role_id']?.message}</FormHelperText>
                            </FormControl>
                            <br />
                            <br />
                            <div className="modal-action">
                                <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth color="error" onClick={form.handleSubmit(handleMoveAndRemoveRole)}>
                                    Move and Remove
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="modal-title">
                                <Typography variant="h6" color="text.primary">
                                    Remove “{role?.display_name}” Role?
                                </Typography>
                            </div>
                            <br />
                            <Typography variant="body2" color="text.primary" style={{textAlign: 'center'}}>
                                Are you sure you want to remove this role? This action cannot be undone.
                            </Typography>
                            <br />
                            <div className="modal-action">
                                <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth color="error" onClick={handleRemoveRole}>
                                    Remove Role
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    )
}
